import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  switchAccount as apiSwitchAccount,
} from './api'
import { fetchWithBootRetry, isDefinitiveUnauthenticated } from './authErrorPolicy'
import {
  ACCOUNT_SWITCH_STORAGE_KEY,
  clearCrossAccountClientState,
  decideSessionResync,
  notifyOtherTabsAccountSwitched,
} from './accountSwitch/accountSwitchLifecycle'
import { resolvePostSwitchRoute } from './accountSwitch/accountSwitchRoute'
import {
  SESSION_IDENTITY_CHANGED_CODE,
  SESSION_IDENTITY_CHANGED_EVENT,
  setExpectedSessionUser,
} from './sessionIdentity'
import type { MeResponse } from './types'

interface AuthState {
  /** `undefined` = not checked yet, `null` = checked, not logged in. */
  me: MeResponse | null | undefined
  loading: boolean
  /** Boot-time `/me` could not be answered (timeout / network / 5xx). The
   * session is NOT known to be invalid, so guards must offer retry instead of
   * redirecting to Login. Only meaningful while `me === undefined`. */
  authUnavailable: boolean
  login: (email: string, password: string) => Promise<MeResponse>
  logout: () => Promise<void>
  switchAccount: (userId: string) => Promise<MeResponse>
  refresh: () => Promise<MeResponse | null>
  /** VistoeraTASK-00586 — apply a fresh `/me` payload (e.g. after nickname save)
   * without rotating the session or forcing a full reload. */
  applyMe: (data: MeResponse) => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [authUnavailable, setAuthUnavailable] = useState(false)
  // Bumped by login/logout so an older in-flight `/me` can never overwrite them.
  const epochRef = useRef(0)
  const inflightRef = useRef<Promise<MeResponse | null> | null>(null)
  const meRef = useRef(me)
  meRef.current = me
  // Written during render so it is current before any child effect requests.
  setExpectedSessionUser(me ? me.user.id : null)

  const refresh = useCallback(() => {
    if (inflightRef.current) return inflightRef.current
    const epoch = epochRef.current
    const booting = meRef.current === undefined
    setLoading(true)
    const run = (async () => {
      try {
        const data = booting
          ? await fetchWithBootRetry(async () => (await fetchMe()).data)
          : (await fetchMe()).data
        if (epoch !== epochRef.current) return meRef.current ?? null
        setMe(data)
        setAuthUnavailable(false)
        return data
      } catch (err) {
        if (epoch !== epochRef.current) return meRef.current ?? null
        if (isDefinitiveUnauthenticated(err)) {
          setMe(null)
          setAuthUnavailable(false)
          return null
        }
        // Timeout / network / 5xx: the session was not rejected, only not
        // checked. Never turn that into "logged out"; keep any in-memory
        // identity, and on boot surface a retryable unavailable state.
        if (meRef.current === undefined) setAuthUnavailable(true)
        return null
      } finally {
        inflightRef.current = null
        setLoading(false)
      }
    })()
    inflightRef.current = run
    return run
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await apiLogin(email, password)
    // VistoeraTASK-00576 - a different account signing in inside the same tab
    // (no page reload) must not inherit the previous identity's Canvas return
    // context / caches.
    if (meRef.current && meRef.current.user.id !== data.user.id) clearCrossAccountClientState()
    epochRef.current += 1
    setMe(data)
    setAuthUnavailable(false)
    // The cookie is browser-wide: other tabs must not keep rendering (and
    // acting as) whoever they showed before.
    notifyOtherTabsAccountSwitched()
    return data
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch (err) {
      // This tab was stale: the browser is now signed in as someone else and
      // the server refused to sign THAT account out. The identity resync
      // (fired by the 409) moves this tab onto the live session instead.
      if ((err as { code?: string } | null)?.code === SESSION_IDENTITY_CHANGED_CODE) return
      // Otherwise always clear local auth on explicit logout even if the network call fails.
    }
    clearCrossAccountClientState()
    epochRef.current += 1
    setMe(null)
    setAuthUnavailable(false)
    notifyOtherTabsAccountSwitched()
  }, [])

  // VistoeraTASK-00563 — deliberately does NOT `setMe`. React must never render
  // the new identity on top of the old URL/workspace (that produced a false
  // "not a member" 403). The caller finishes with ONE full page load, whose
  // normal boot path (`GET /me`) establishes the new identity.
  const switchAccount = useCallback(async (userId: string) => {
    const { data } = await apiSwitchAccount(userId)
    return data
  }, [])

  const applyMe = useCallback((data: MeResponse) => {
    // Same session identity only — never use this to switch accounts.
    if (meRef.current && meRef.current.user.id !== data.user.id) return
    setMe(data)
  }, [])

  useEffect(() => {
    let syncing = false
    // The shared browser session may now be another identity (other tab's
    // login/logout/switch, or a 409 `session_identity_changed`). Read it
    // WITHOUT touching React state (no "new me + old URL" render); only a real
    // identity change does one full load on the mapped page.
    const resync = () => {
      if (syncing) return
      syncing = true
      const pathBeforeSync = window.location.pathname
      const rendered = meRef.current ? meRef.current.user.id : null
      const apply = (data: MeResponse | null) => {
        const action = decideSessionResync(rendered, data ? data.user.id : null)
        if (action === 'keep') {
          syncing = false
          return
        }
        clearCrossAccountClientState()
        window.location.replace(!data ? '/login' : resolvePostSwitchRoute(pathBeforeSync, data))
      }
      void fetchMe().then(
        ({ data }) => apply(data),
        (err) => {
          if (isDefinitiveUnauthenticated(err)) apply(null)
          // Transient outage: keep this tab as is. The identity header still
          // stops it from acting as another account; the next call re-checks.
          else syncing = false
        },
      )
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === ACCOUNT_SWITCH_STORAGE_KEY) resync()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(SESSION_IDENTITY_CHANGED_EVENT, resync)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(SESSION_IDENTITY_CHANGED_EVENT, resync)
    }
  }, [])

  const value = useMemo(
    () => ({ me, loading, authUnavailable, login, logout, switchAccount, refresh, applyMe }),
    [me, loading, authUnavailable, login, logout, switchAccount, refresh, applyMe],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
